<template>
  <div class="camera-widget-container" ref="widgetContainer">
    <div class="qwen-multiangle-container">
      <div class="qwen-multiangle-canvas" ref="canvasContainer"></div>
      <!-- 底部参数信息栏 -->
      <div class="qwen-multiangle-info">
        <div class="qwen-multiangle-param">
          <div class="qwen-multiangle-param-label">水平</div>
          <div class="qwen-multiangle-param-value azimuth">{{ Math.round(cameraState.azimuth) }}°</div>
        </div>
        <div class="qwen-multiangle-param">
          <div class="qwen-multiangle-param-label">垂直</div>
          <div class="qwen-multiangle-param-value elevation">{{ Math.round(cameraState.elevation) }}°</div>
        </div>
        <div class="qwen-multiangle-param">
          <div class="qwen-multiangle-param-label">缩放</div>
          <div class="qwen-multiangle-param-value zoom">{{ parseFloat(cameraState.distance).toFixed(1) }}</div>
        </div>
        <button class="qwen-multiangle-reset" @click="resetCamera" title="重置相机">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="1 4 1 10 7 10"></polyline>
            <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"></path>
          </svg>
        </button>
      </div>
      <!-- 滚轮提示 -->
      <div class="qwen-multiangle-hint">
        拖拽立方体旋转 · 滚轮缩放
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CameraWidget3D',
  props: {
    imageUrl: {
      type: String,
      default: null
    },
    // 初始相机参数
    initialParams: {
      type: Object,
      default: () => ({ horizontal_angle: 0, vertical_angle: 0, zoom: 5 })
    }
  },
  data() {
    return {
      cameraState: {
        azimuth: this.initialParams?.horizontal_angle || 0,
        elevation: this.initialParams?.vertical_angle || 0,
        distance: this.initialParams?.zoom || 5
      },
      selectedAzimuth: 0,
      selectedElevation: 0,
      selectedDistance: 4,
      azimuthOptions: [
        { label: '正面视角', value: 0 },
        { label: '右前方视角', value: 45 },
        { label: '右侧视角', value: 90 },
        { label: '右后方视角', value: 135 },
        { label: '背面视角', value: 180 },
        { label: '左后方视角', value: 225 },
        { label: '左侧视角', value: 270 },
        { label: '左前方视角', value: 315 }
      ],
      elevationOptions: [
        { label: '仰拍', value: -30 },
        { label: '平视', value: 0 },
        { label: '高角度', value: 30 },
        { label: '俯拍', value: 60 }
      ],
      distanceOptions: [
        { label: '远景', value: 1 },
        { label: '中景', value: 4 },
        { label: '特写', value: 8 }
      ],
      cameraWidget: null,
      // 防止 setState 触发 onStateChange 导致循环
      isSettingParamsLock: false
    }
  },
  computed: {
  },
  watch: {
    imageUrl(newUrl) {
      if (this.cameraWidget) {
        this.cameraWidget.updateImage(newUrl)
      }
    },
    // 监听初始参数变化
    initialParams: {
      handler(newParams) {
        if (newParams && this.cameraWidget) {
          this.setCameraParams(newParams)
        }
      },
      deep: true
    }
  },
  mounted() {
    this.initCameraWidget()
  },
  beforeDestroy() {
    if (this.cameraWidget) {
      this.cameraWidget.dispose()
    }
  },
  methods: {
    async initCameraWidget() {
      // 动态导入 three.js 和 CameraWidget
      try {
        const THREE = await import('three')
        window.THREE = THREE
        // 从 initialParams 获取初始参数
        const initAzimuth = this.initialParams?.horizontal_angle || 0
        const initElevation = this.initialParams?.vertical_angle || 0
        const initDistance = this.initialParams?.zoom || 5
        // 更新 cameraState
        this.cameraState = {
          azimuth: initAzimuth,
          elevation: initElevation,
          distance: initDistance
        }
        // 导入样式和 CameraWidget
        const { CameraWidget } = await import('./CameraWidget3DCore.js')
        this.cameraWidget = new CameraWidget({
          container: this.$refs.canvasContainer,
          initialState: {
            azimuth: initAzimuth,
            elevation: initElevation,
            distance: initDistance,
            imageUrl: this.imageUrl
          },
          onStateChange: this.handleStateChange
        })
        // 初始化完成后，若 imageUrl 已有值则主动更新一次（修复弹窗重建时图片丢失问题）
        if (this.imageUrl) {
          this.cameraWidget.updateImage(this.imageUrl)
        }
        console.log('CameraWidget3D 初始化完成，参数:', { azimuth: initAzimuth, elevation: initElevation, distance: initDistance })
      } catch (error) {
        console.error('初始化3D相机控制失败:', error)
      }
    },
    handleStateChange(state) {
      // 如果当前正在通过 setCameraParams 设置参数，跳过 emit 避免循环
      if (this.isSettingParamsLock) {
        // 只更新本地显示状态，不通知父组件
        this.$set(this.cameraState, 'azimuth', state.azimuth)
        this.$set(this.cameraState, 'elevation', state.elevation)
        this.$set(this.cameraState, 'distance', state.distance)
        return
      }
      this.$set(this.cameraState, 'azimuth', state.azimuth)
      this.$set(this.cameraState, 'elevation', state.elevation)
      this.$set(this.cameraState, 'distance', state.distance)
      // 发射事件给父组件
      this.$emit('camera-change', {
        horizontal_angle: Math.round(state.azimuth),
        vertical_angle: Math.round(state.elevation),
        zoom: parseFloat(state.distance.toFixed(1))
      })
    },
    // 公开方法，供父组件调用
    getCameraParams() {
      return {
        horizontal_angle: Math.round(this.cameraState.azimuth),
        vertical_angle: Math.round(this.cameraState.elevation),
        zoom: parseFloat(this.cameraState.distance.toFixed(1))
      }
    },
    // 设置相机参数（供父组件调用）
    setCameraParams(params) {
      if (this.cameraWidget) {
        const newState = {
          azimuth: params.horizontal_angle !== undefined ? params.horizontal_angle : this.cameraState.azimuth,
          elevation: params.vertical_angle !== undefined ? params.vertical_angle : this.cameraState.elevation,
          distance: params.zoom !== undefined ? params.zoom : this.cameraState.distance
        }
        // 加锁，防止 setState 触发 onStateChange 导致循环
        this.isSettingParamsLock = true
        this.cameraWidget.setState(newState)
        // 使用 $set 逐属性更新，确保响应式追踪可靠
        this.$set(this.cameraState, 'azimuth', newState.azimuth)
        this.$set(this.cameraState, 'elevation', newState.elevation)
        this.$set(this.cameraState, 'distance', newState.distance)
        this.isSettingParamsLock = false
        console.log('CameraWidget3D 参数已更新:', newState)
      }
    },
    resetCamera() {
      const resetState = {
        azimuth: 0,
        elevation: 0,
        distance: 5
      }
      this.cameraState = { ...resetState }
      if (this.cameraWidget) {
        this.cameraWidget.setState(resetState)
      }
      this.$emit('camera-change', {
        horizontal_angle: 0,
        vertical_angle: 0,
        zoom: 5
      })
    }
  }
}
</script>

<style lang="less" scoped>
.camera-widget-container {
  width: 100%;
  height: 100%;
}

.qwen-multiangle-container {
  width: 100%;
  height: 100%;
  position: relative;
  background: #1a1a1a;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  border-radius: 8px;
  overflow: hidden;
}

.qwen-multiangle-canvas {
  width: 100%;
  height: 100%;
  display: block;
}

.qwen-multiangle-prompt {
  position: absolute;
  top: 8px;
  left: 8px;
  right: 8px;
  background: rgba(10, 10, 15, 0.9);
  border: 1px solid rgba(233, 61, 130, 0.3);
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 11px;
  color: #E93D82;
  backdrop-filter: blur(4px);
  font-family: 'Consolas', 'Monaco', monospace;
  word-break: break-all;
  line-height: 1.4;
}

.qwen-multiangle-info {
  position: absolute;
  bottom: 8px;
  left: 8px;
  right: 8px;
  background: rgba(10, 10, 15, 0.9);
  border: 1px solid rgba(233, 61, 130, 0.3);
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 11px;
  color: #e0e0e0;
  display: flex;
  justify-content: space-around;
  align-items: center;
  backdrop-filter: blur(4px);
}

.qwen-multiangle-param {
  text-align: center;
}

.qwen-multiangle-param-label {
  color: #888;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.qwen-multiangle-param-value {
  font-weight: 600;
  font-size: 13px;
}

.qwen-multiangle-param-value.azimuth {
  color: #E93D82;
}

.qwen-multiangle-param-value.elevation {
  color: #00FFD0;
}

.qwen-multiangle-param-value.zoom {
  color: #FFB800;
}

.qwen-multiangle-reset {
  width: 24px;
  height: 24px;
  border-radius: 4px;
  border: 1px solid rgba(233, 61, 130, 0.4);
  background: rgba(10, 10, 15, 0.8);
  color: #E93D82;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.qwen-multiangle-reset:hover {
  background: rgba(233, 61, 130, 0.2);
  border-color: #E93D82;
}

.qwen-multiangle-reset:active {
  transform: scale(0.95);
}

.qwen-multiangle-dropdowns {
  position: absolute;
  top: 40px;
  left: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  z-index: 10;
}

.qwen-multiangle-dropdown {
  display: flex;
  align-items: center;
  gap: 6px;
}

.qwen-multiangle-dropdown-label {
  font-size: 9px;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  min-width: 28px;
  text-align: right;
  white-space: nowrap;
}

.qwen-multiangle-dropdown-label.azimuth {
  color: #E93D82;
}

.qwen-multiangle-dropdown-label.elevation {
  color: #00FFD0;
}

.qwen-multiangle-dropdown-label.distance {
  color: #FFB800;
}

.qwen-multiangle-select {
  background: rgba(10, 10, 15, 0.9);
  border: 1px solid rgba(100, 100, 120, 0.4);
  border-radius: 4px;
  padding: 3px 6px;
  font-size: 10px;
  color: #e0e0e0;
  cursor: pointer;
  outline: none;
  min-width: 120px;
  backdrop-filter: blur(4px);
}

.qwen-multiangle-select:hover {
  border-color: rgba(150, 150, 170, 0.6);
}

.qwen-multiangle-select:focus {
  border-color: #E93D82;
}

.qwen-multiangle-select.azimuth:focus {
  border-color: #E93D82;
}

.qwen-multiangle-select.elevation:focus {
  border-color: #00FFD0;
}

.qwen-multiangle-select.distance:focus {
  border-color: #FFB800;
}

.qwen-multiangle-select option {
  background: #1a1a2e;
  color: #e0e0e0;
}

/* 操作提示 */
.qwen-multiangle-hint {
  position: absolute;
  top: 8px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.5);
  border-radius: 4px;
  padding: 4px 12px;
  font-size: 11px;
  color: #888;
  pointer-events: none;
  user-select: none;
  backdrop-filter: blur(4px);
}
</style>
